import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import LandingImage from "../../images/CasperDeBock.png";
import { 
  Github, 
  Linkedin, 
  Facebook, 
  Instagram, 
  ArrowRight, 
  Code, 
  Layers, 
  MonitorSmartphone,
  ChevronDown
} from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import {
  Card,
  CardContent,
} from "../ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";

const technologies = [
  "React", "JavaScript", "TypeScript", "Node.js", "TailwindCSS", "Next.js"
];

const Landing = () => {
  const [scrollY, setScrollY] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);

    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.15,
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  const imageVariants = {
    hidden: { scale: 0.9, opacity: 0 },
    visible: { 
      scale: 1,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, delay: 0.2 }
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-zinc-100 via-white to-zinc-100 dark:from-zinc-900 dark:via-zinc-900 dark:to-zinc-800 overflow-hidden">
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 right-0 w-1/3 h-1/3 bg-gradient-to-tl from-blue-500/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-gradient-to-tr from-violet-500/10 to-transparent rounded-full blur-3xl"></div>
        <div className="absolute -top-40 -left-40 w-80 h-80 border border-zinc-200 dark:border-zinc-700/50 rounded-full"></div>
        <div className="absolute top-1/4 right-1/4 w-64 h-64 border border-zinc-200 dark:border-zinc-700/50 rounded-full"></div>
        <svg 
          className="absolute top-0 left-0 w-screen h-screen opacity-5 dark:opacity-[0.02]" 
          viewBox="0 0 100 100" 
          preserveAspectRatio="none"
        >
          <defs>
            <pattern 
              id="grid" 
              width="8" 
              height="8" 
              patternUnits="userSpaceOnUse"
            >
              <path 
                d="M 8 0 L 0 0 0 8" 
                fill="none" 
                stroke="currentColor" 
                strokeWidth="0.5" 
                className="text-zinc-700"
              />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-16">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
          {/* Left Column: Text & CTAs */}
          <motion.div 
            className="lg:col-span-7 space-y-8"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            {/* Tags */}
            <motion.div variants={itemVariants} className="flex flex-wrap gap-2">
              <Badge variant="outline" className="bg-zinc-100/80 dark:bg-zinc-800/80 backdrop-blur-sm text-zinc-800 dark:text-zinc-200 border-zinc-200 dark:border-zinc-700 px-3 py-1">
                Software Developer
              </Badge>
              <Badge variant="outline" className="bg-zinc-100/80 dark:bg-zinc-800/80 backdrop-blur-sm text-zinc-800 dark:text-zinc-200 border-zinc-200 dark:border-zinc-700 px-3 py-1">
                UI/UX Designer
              </Badge>
              <Badge variant="outline" className="bg-zinc-100/80 dark:bg-zinc-800/80 backdrop-blur-sm text-zinc-800 dark:text-zinc-200 border-zinc-200 dark:border-zinc-700 px-3 py-1">
                Problem Solver
              </Badge>
            </motion.div>

            {/* Headline */}
            <motion.div variants={itemVariants} className="space-y-4">
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-zinc-900 dark:text-white">
                I'm <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-violet-600">Casper</span>, <br className="hidden sm:block" />
                <span className="relative">
                  a software developer
                  <span className="absolute -bottom-1 left-0 right-0 h-1 bg-gradient-to-r from-blue-600 to-violet-600 rounded-full"></span>
                </span>
              </h1>
              
              <p className="text-lg text-zinc-700 dark:text-zinc-300 max-w-xl">
                I create exceptional digital experiences that solve complex problems with clean, efficient, and maintainable code.
              </p>
            </motion.div>


            {/* CTAs */}
            <motion.div variants={itemVariants} className="flex flex-col sm:flex-row gap-4 pt-4">
              <Button asChild size="lg" className="bg-gradient-to-r from-blue-600 to-violet-600 hover:from-blue-700 hover:to-violet-700 text-white rounded-lg shadow-lg transition-all hover:shadow-blue-500/25">
                <Link to="/contact" className="flex items-center gap-2">
                  Let's Connect
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </Button>
              
              <Button 
                asChild 
                variant="outline" 
                size="lg" 
                className="rounded-lg border-2 border-zinc-200 dark:border-zinc-700 bg-white/80 dark:bg-zinc-800/80 backdrop-blur-sm hover:bg-white dark:hover:bg-zinc-800 transition-all text-zinc-900 dark:text-zinc-100"
                >
                <Link to="/projects" className="flex items-center gap-2">
                    View My Projects
                </Link>
                </Button>
            </motion.div>

            {/* Social links */}
            <motion.div variants={itemVariants} className="flex items-center gap-4 pt-4">
              <a 
                href="https://github.com/CasperDeBock" 
                target="_blank" 
                rel="noreferrer"
                className="p-2 rounded-full bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                aria-label="GitHub"
              >
                <Github className="h-5 w-5" />
              </a>
              <a 
                href="https://www.linkedin.com/in/casperdebock/" 
                target="_blank" 
                rel="noreferrer"
                className="p-2 rounded-full bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                aria-label="LinkedIn"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a 
                href="https://www.facebook.com/casper.debock/" 
                target="_blank" 
                rel="noreferrer"
                className="p-2 rounded-full bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                aria-label="Facebook"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a 
                href="https://www.instagram.com/casperdebock" 
                target="_blank" 
                rel="noreferrer"
                className="p-2 rounded-full bg-zinc-100 dark:bg-zinc-800 hover:bg-zinc-200 dark:hover:bg-zinc-700 transition-colors text-zinc-600 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100"
                aria-label="Instagram"
              >
                <Instagram className="h-5 w-5" />
              </a>
            </motion.div>
          </motion.div>

          {/* Right Column: Image & Summary */}
          <motion.div 
            className="lg:col-span-5"
            initial="hidden"
            animate="visible"
            variants={containerVariants}
          >
            <div className="relative">
              {/* Glowing background effect */}
              <div className="absolute inset-0 -translate-y-8 bg-gradient-to-r from-blue-600/20 to-violet-600/20 rounded-full blur-3xl"></div>
              
              {/* Image */}
              <motion.div 
                variants={imageVariants}
                className="relative rounded-2xl overflow-hidden border-2 border-zinc-200/50 dark:border-zinc-700/50 bg-white/50 dark:bg-zinc-800/50 backdrop-blur-sm shadow-xl"
              >
                <img 
                  src={LandingImage} 
                  alt="Casper De Bock" 
                  className="w-full h-auto object-cover rounded-xl"
                />
                
                {/* Overlaid card */}
                <div className="absolute bottom-4 left-4 right-4">
                  <Card className="bg-white/90 dark:bg-zinc-900/90 backdrop-blur-md border-zinc-200/50 dark:border-zinc-700/50 shadow-lg">
                    <CardContent className="p-4">
                      <Tabs defaultValue="frontend" className="w-full">
                        <TabsList className="w-full bg-zinc-100 dark:bg-zinc-800/50">
                          <TabsTrigger value="frontend" className="flex-1">Frontend</TabsTrigger>
                          <TabsTrigger value="backend" className="flex-1">Backend</TabsTrigger>
                          <TabsTrigger value="design" className="flex-1">Design</TabsTrigger>
                        </TabsList>
                        <TabsContent value="frontend" className="mt-2">
                          <div className="flex items-center gap-3">
                            <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full">
                              <Code className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">React, Blazor, Vue, Next.js</p>
                              <p className="text-xs text-zinc-500 dark:text-zinc-400">Modern web frontends</p>
                            </div>
                          </div>
                        </TabsContent>
                        <TabsContent value="backend" className="mt-2">
                          <div className="flex items-center gap-3">
                            <div className="bg-green-100 dark:bg-green-900/30 p-2 rounded-full">
                              <Layers className="h-5 w-5 text-green-600 dark:text-green-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">.NET, Node.js, Express, MongoDB</p>
                              <p className="text-xs text-zinc-500 dark:text-zinc-400">Scalable backend solutions</p>
                            </div>
                          </div>
                        </TabsContent>
                        <TabsContent value="design" className="mt-2">
                          <div className="flex items-center gap-3">
                            <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-full">
                              <MonitorSmartphone className="h-5 w-5 text-purple-600 dark:text-purple-400" />
                            </div>
                            <div>
                              <p className="text-sm font-medium">Figma, Tailwind CSS</p>
                              <p className="text-xs text-zinc-500 dark:text-zinc-400">UI/UX design systems</p>
                            </div>
                          </div>
                        </TabsContent>
                      </Tabs>
                    </CardContent>
                  </Card>
                </div>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll indicator - centered both horizontally and vertically */}
        <motion.div 
        className="absolute left-1/2 bottom-8 transform -translate-x-1/2 flex flex-col items-center text-zinc-500 dark:text-zinc-400"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1, y: [0, 10, 0] }}
        transition={{
            delay: 2,
            y: {
            repeat: Infinity,
            duration: 2,
            },
        }}
        >
        <span className="text-sm font-medium mb-2">Scroll down</span>
        <ChevronDown className="h-5 w-5" />
        </motion.div>
    </div>
  );
};

export default Landing;